import styled from "@emotion/styled";
import { PageLayout } from "../../shared/layouts/PageLayout";
import { adminDashboardBreadcrumbs } from "@features/AdminDashboard/admin-dashboard-breadcrumbs";
import { UserThreadLogs } from "@features/UserLogs/UserThreadLogs";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminGetUserThreads } from "@queries/chat-logs/useAdminGetUserThreads";
import { useGetUser } from "@queries/users/useGetUser";
import { UserLogsFiltersNavbar } from "@features/UserLogs/UserLogsFiltersNavbar";
import { UserThreads } from "@features/UserLogs/UserThreads";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAdminGetUserThreadLogs } from "@queries/chat-logs/useAdminGetUserThreadLogs";
import { UserThreadsMetadataResults } from "@features/UserLogs/UserThreadsMetadataResults";
import { useInfiniteScroll } from "@shared/hooks/useInfiniteScroll";
import { CircularProgress, Typography } from "@mui/material";
import { isUuid } from "@shared/utils/validators";
import { toast } from "@shared/services/toast";

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
`;

export function UserLogsPage() {
  const { userId = "", threadId = null } = useParams<{
    userId: string;
    threadId: string;
  }>();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const [threadIdFilter, setThreadIdFilter] = useState("");

  const {
    data: userThreadsData,
    isLoading: isLoadingUserThreads,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useAdminGetUserThreads({
    userId: userId,
    enabled: !threadId,
  });

  const userThreads =
    userThreadsData?.pages.map((page) => page.threads).flat() ?? [];

  const threadsMetadata = userThreadsData?.pages.length
    ? {
        page: userThreadsData.pages[0].page,
        limit: userThreadsData.pages[0].limit,
        total: userThreadsData.pages[0].total,
      }
    : null;

  useEffect(() => {
    if (threadId) {
      setThreadIdFilter("");
    }
  }, [threadId]);

  const userThreadLogsQuery = useAdminGetUserThreadLogs({
    userId: userId,
    threadId: threadId ?? threadIdFilter,
    onError: () => {
      if (threadIdFilter) {
        toast.error("Thread not found.", {
          toastId: "thread-not-found",
          autoClose: 3000,
        });
        setThreadIdFilter("");
      }
    },
  });

  const userThreadLogs = userThreadLogsQuery.data?.logs ?? [];
  const userThread = userThreadLogsQuery.data?.thread;

  const userQuery = useGetUser({
    id: userId,
  });
  const user = userQuery.data;

  const breadcrumbs = useMemo(() => {
    const current = {
      label: threadId ? threadId : "Threads",
    };

    const previous = [
      adminDashboardBreadcrumbs.dashboard,
      adminDashboardBreadcrumbs.users,
      {
        label: user?.name || user?.email || "",
        link: `/admin/users/${userId}`,
      },
    ];

    if (threadId) {
      previous.push({
        label: "Threads",
        link: `/admin/users/${userId}/thread-logs`,
      });
    }

    return {
      current,
      previous,
    };
  }, [user, userId, threadId]);

  const handleThreadLogsClick = useCallback(
    (threadId: string) => {
      navigate(`/admin/users/${userId}/thread-logs/${threadId}`);
    },
    [userId, navigate]
  );

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage: () => fetchNextPage && fetchNextPage(),
  });

  const hasThreadIdFilter = threadIdFilter && userThreadLogsQuery.data;

  return (
    <PageLayout breadcrumbs={breadcrumbs} ref={pageRef}>
      <Container ref={pageRef}>
        {!threadId && (
          <>
            <UserLogsFiltersNavbar
              updateUserThreadsQueryParams={(query) => {
                if (query.threadIdFilter && isUuid(query.threadIdFilter)) {
                  setThreadIdFilter(query.threadIdFilter);
                } else {
                  setThreadIdFilter("");
                }
              }}
              defaultValues={{
                threadIdFilter,
              }}
              isLoading={isLoadingUserThreads || userThreadLogsQuery.isLoading}
            />
            {!hasThreadIdFilter && (
              <UserThreadsMetadataResults threadsMetadata={threadsMetadata} />
            )}
            <UserThreads
              isLoadingThreads={isLoadingUserThreads}
              threads={
                hasThreadIdFilter
                  ? [userThreadLogsQuery.data.thread]
                  : userThreads
              }
              handleThreadLogsClick={handleThreadLogsClick}
            />
            <div ref={sentinelRef} />
            {isLoadingUserThreads ||
              (isFetchingNextPage && (
                <LoaderContainer>
                  <CircularProgress />
                </LoaderContainer>
              ))}
            {!hasNextPage && (
              <Typography textAlign={"center"} mt={6}>
                End
              </Typography>
            )}
          </>
        )}

        {!!threadId && (
          <UserThreadLogs
            userId={userId}
            threadId={threadId}
            threadTitle={userThread?.title ?? ""}
            threadLogs={userThreadLogs}
            isLoadingThreadLogs={userThreadLogsQuery.isLoading}
          />
        )}
      </Container>
    </PageLayout>
  );
}
